import {
  AdminSpinner,
  Button,
  Div,
  ErrorToast,
  H3,
  Modal,
  ModalFullScreen,
  P,
  PropsModal,
  SuccessToast,
  Tab,
  TabsWrapper,
  cancelMenuImportFromUrl,
  importMenuFromAnotherProvider,
  importMenuFromAnotherProviderPreview,
  importMenuFromURL,
  lightTheme,
  triggerNotification,
  reportError,
} from '@vgrubs/components';
import { ImportMenuV2Dto } from 'components/src/types/api/menu.types';
import { MenuV2Response } from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router';
import { Availability } from './MenuProviderSectionsPreview/Availability/Availability';
import { Categories } from './MenuProviderSectionsPreview/Categories/Categories';
import { Items } from './MenuProviderSectionsPreview/Items/Items';
import { Modifiers } from './MenuProviderSectionsPreview/Modifiers/Modifiers';
import useMenuScrappingSocket from './MenuScrappingSocket';
import { ImportLoaderModal } from './components/ImportLoaderModal';
import { useSelectedMenu } from './menuSelection';

const menuImportNotificationMessages = {
  importingSuccess: 'Menu Successfully Imported to the store!',
  importingFailed: 'Menu Importing Failed.',

  scrappingSuccess: 'Menu Successfully Fetched!',
  scrappingFailed: 'Menu Scrapping Failed.',

  menuPullSuccess: 'Menu Successfully Pulled to the store!',
};

export const isMenuProviderDelivery = (provider: Provider) =>
  provider === Provider.delivery;

export const MenuImportPreview = ({
  importProvider,
  scrappingUrl,
  setScrappingUrl,
  closeModal,
  storeName,
  setMenuMain,
  menu,
  setMenu,
  importSource,
  handleGetSyncStatus,
  handleChangeShouldPublish,
}: PropsModal & {
  importProvider: Provider | string | null;
  scrappingUrl: string | null;
  setScrappingUrl: Dispatch<SetStateAction<string | null>>;
  storeName: string;
  setMenuMain: Dispatch<SetStateAction<MenuV2Response | null>>;
  menu: MenuV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  importSource?: { locationId: number; brandId: number | null } | null;
  handleGetSyncStatus: (silent?: boolean) => void;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [menuTab, setMenuTab] = useState(1);
  const [areYouSureModal, setAreYouSureModal] = useState(false);
  const [areYouSureModalCancel, setAreYouSureModalCancel] = useState(false);
  const { locationId, brandId } = useParams() as {
    locationId: string;
    brandId: string;
    provider: Provider;
  };
  const { menuId, menu: selectedMenu } = useSelectedMenu();
  const [modalLoader, setModalLoader] = useState(false);
  const [modalLoaderProgress, setModalLoaderProgress] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);

  const scrappingUrlRef = useRef(scrappingUrl);
  const storeNameRef = useRef(storeName);

  useEffect(() => {
    scrappingUrlRef.current = scrappingUrl;
  }, [scrappingUrl]);

  useEffect(() => {
    storeNameRef.current = storeName;
  }, [storeName]);

  const triggerScrapping = useCallback(() => {
    const url = scrappingUrlRef.current;
    handleUrlScrapping(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerCancelScrapping = useCallback(() => {
    const url = scrappingUrlRef.current;
    handleCancelPreview(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerNotificationWithStore = (text: string) => {
    const store = `Store Name: ${storeNameRef.current}`;
    triggerNotification(text, store);
  };

  const { observeMenu } = useMenuScrappingSocket({
    triggerScrapping,
    setModalLoaderProgress,
    setLoadProgress,
    onScrapeFail: () => {
      triggerNotificationWithStore(
        menuImportNotificationMessages.scrappingFailed
      );
      ErrorToast(menuImportNotificationMessages.scrappingFailed);
    },

    // onImportFail: () => {
    //   triggerNotificationWithStore(
    //     menuImportNotificationMessages.importingFailed
    //   );
    //   ErrorToast(menuImportNotificationMessages.importingFailed);
    //   setModalLoader(false);

    //   // observeMenuImport(locationId, brandId, false);

    //   setMenu(null);
    //   closeModal?.();
    // },

    // onImportSuccess: () => {
    //   triggerNotificationWithStore(
    //     menuImportNotificationMessages.importingSuccess
    //   );
    //   SuccessToast(menuImportNotificationMessages.importingSuccess);
    //   setModalLoader(false);
    //   handleGetMenu();

    //   // observeMenuImport(locationId, brandId, false);

    //   setMenu(null);
    //   closeModal?.();
    // },
  });

  async function handleGetMenuImport() {
    setMenuTab(1);
    try {
      setLoading(true);
      const { data } = await importMenuFromAnotherProviderPreview(
        locationId,
        brandId,
        importProvider as Provider
      );

      setMenu(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCancelPreview(url: string | null) {
    if (url) {
      await cancelMenuImportFromUrl(locationId, brandId, url);
    }
    setScrappingUrl(null);
  }

  async function handleUrlScrapping(url: string | null) {
    if (!url) throw new Error('Url is required');
    setMenuTab(1);
    setLoadProgress(0);

    try {
      setLoading(true);
      const { data } = await importMenuFromURL(locationId, brandId, url);

      if (data.data.started && !data.data.finished) {
        setModalLoaderProgress(true);
        observeMenu(locationId, brandId, true);
      }

      if (data.data.finished) {
        triggerNotificationWithStore(
          menuImportNotificationMessages.scrappingSuccess
        );
        setModalLoaderProgress(false);
        observeMenu(locationId, brandId, false);
        setLoadProgress(0);
      }

      setMenu(data.data.menu);
    } catch (error) {
      setScrappingUrl(null);
      triggerNotificationWithStore(
        menuImportNotificationMessages.scrappingFailed
      );
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmitImportMenu() {
    if (!menu) return;

    const body: ImportMenuV2Dto = {
      categories: menu.categories.map((category) => ({
        name: category.name,
        description: category.description || '',
        scheduleIds: category.schedules.map((schedule) => schedule.id),
        items: category.items.map((item) => ({
          menuItem: {
            name: item.name,
            description: item.description || '',
            price: item.price,
            pickupPrice: item.pickupPrice || 0,
            categoryId: category.id,
            modifierGroupIds: item.modifierGroups.map(
              (modifierGroup) => modifierGroup.id
            ),
            customizedPrices: item.customizedPrices,
            availability: item.availability,
            preparationNotes: item.preparationNotes || null,
          },
          imageUrl: item.imageUrl || null,
        })),
      })),
      modifierGroups: menu.modifierGroups.map((modifierGroup) => ({
        id: modifierGroup.id,
        name: modifierGroup.name,
        description: modifierGroup.description || '',
        type: modifierGroup.type,
        options: modifierGroup.options.map((option) => ({
          name: option.name,
          description: option.description || '',
          price: option.price,
          availability: option.availability,
        })),
        required: modifierGroup.required,
        selectionsMin: modifierGroup.selectionsMin,
        selectionsMax: modifierGroup.selectionsMax,
      })),
      schedules: menu.availability.map((schedule) => ({
        id: schedule.id,
        name: schedule.name,
        hours: schedule.hours.map((hour) => ({
          day: hour.day,
          startTime: hour.startTime,
          endTime: hour.endTime,
        })),
      })),
      previewUrl: scrappingUrl ? scrappingUrl : null,
      locationId: importSource?.locationId ?? null,
      brandId: importSource?.brandId ?? null,
    };

    try {
      setAreYouSureModal(false);
      setLoading(true);
      const { data } = await importMenuFromAnotherProvider(
        locationId,
        brandId,
        body,
        menuId
      );
      setMenuMain(data.data);
      SuccessToast(menuImportNotificationMessages.importingSuccess);
      setLoading(false);
      setMenu(null);
      closeModal?.();
      handleGetSyncStatus(true);
      handleChangeShouldPublish(true);
    } catch (error) {
      setScrappingUrl(null);
      triggerNotificationWithStore(
        menuImportNotificationMessages.importingFailed
      );
      ErrorToast(menuImportNotificationMessages.importingFailed);
    } finally {
      triggerCancelScrapping();
    }
  }

  useEffect(() => {
    if (importProvider && importProvider !== 'Menu Link') handleGetMenuImport();
    if (scrappingUrl && importProvider === 'Menu Link') triggerScrapping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importProvider, scrappingUrl]);

  const handleCancel = () => {
    setMenu(null);
    closeModal?.();
    setAreYouSureModalCancel(false);
    triggerCancelScrapping();
  };

  const ImportButton = () => {
    return (
      <Button $selected $wSM="100%" onClick={() => setAreYouSureModal(true)}>
        Import
      </Button>
    );
  };

  const CancelButton = () => {
    return (
      <Button
        $selected
        $gray
        $wSM="100%"
        onClick={() => setAreYouSureModalCancel(true)}
      >
        Cancel
      </Button>
    );
  };

  return (
    <>
      {loading && <AdminSpinner />}
      <ModalFullScreen
        height="calc(100vh - 150px)"
        isOpen={menu ? true : false}
        closeModal={() => null}
        header={'Menu Preview'}
        exitButton={false}
        $numOfFloatingButtons={2}
        footer={
          <Div
            $flex
            $justifyEnd
            $gap="20px"
            $columnSM
            $gapSM="16px"
            $widthSM="100%"
          >
            <CancelButton />
            <ImportButton />
          </Div>
        }
      >
        <Div>
          <>
            <TabsWrapper style={{ paddingTop: 0 }}>
              <Tab selected={menuTab === 1} onClick={() => setMenuTab(1)}>
                Items
              </Tab>
              <Tab selected={menuTab === 2} onClick={() => setMenuTab(2)}>
                Modifiers
              </Tab>
              <Tab selected={menuTab === 3} onClick={() => setMenuTab(3)}>
                Categories
              </Tab>
              <Tab selected={menuTab === 4} onClick={() => setMenuTab(4)}>
                Availability
              </Tab>
            </TabsWrapper>

            <>
              {menuTab === 1 && (
                <Items menu={menu} setMenu={setMenu} setLoading={setLoading} />
              )}
              {menuTab === 2 && (
                <Modifiers
                  menu={menu}
                  setMenu={setMenu}
                  setLoading={setLoading}
                />
              )}
              {menuTab === 3 && <Categories menu={menu} setMenu={setMenu} />}
              {menuTab === 4 && (
                <Availability
                  menu={menu}
                  setMenu={setMenu}
                  setLoading={setLoading}
                />
              )}
            </>
          </>
        </Div>
      </ModalFullScreen>

      <Modal
        isOpen={areYouSureModal}
        closeModal={() => {
          setAreYouSureModal(false);
        }}
        logo={false}
        zIndex={110}
      >
        <H3 $mb="24" $color={lightTheme.primary}>
          Are you sure
        </H3>
        <P $mb="40">
          This replaces everything in
          {selectedMenu ? ` the "${selectedMenu.name}" menu` : ' the menu'} with
          the imported content.
          {selectedMenu && ' Other menus of this brand are not touched.'} This
          cannot be undone.
        </P>

        <Div $flex $gap="16px">
          <Button onClick={handleSubmitImportMenu} $w="100%" $selected>
            Import
          </Button>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setAreYouSureModal(false)}
          >
            Cancel
          </Button>
        </Div>
      </Modal>

      <Modal
        isOpen={areYouSureModalCancel}
        closeModal={() => {
          setAreYouSureModalCancel(false);
        }}
        logo={false}
        zIndex={110}
      >
        <H3 $mb="24" $color={lightTheme.primary}>
          Are you sure
        </H3>
        <P $mb="40">
          Are you sure you want to do cancel importing? This cannot be undone.
        </P>

        <Div $flex $gap="16px">
          <Button onClick={handleCancel} $w="100%" $selected>
            Yes
          </Button>
          <Button
            $selected
            $gray
            $w="100%"
            onClick={() => setAreYouSureModalCancel(false)}
          >
            No
          </Button>
        </Div>
      </Modal>

      <ImportLoaderModal loading={modalLoader} setLoading={setModalLoader} />

      <ImportLoaderModal
        loading={modalLoaderProgress}
        setLoading={setModalLoaderProgress}
        loadProgress={loadProgress}
        progress
      />
    </>
  );
};
